import React from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { useThemeContext } from '@/contexts/ThemeContext'
import { Seo, getBasicPageMetaData } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'

// filePath: 'pages/[locale]/sketchapp/index.tsx'
// App store redirect page for the Sketch app
interface SketchAppRedirectProps {
  locale: string
}

const APP_STORE_URLS = {
  apple: 'https://apps.apple.com/us/app/sketch-aug-6th/id6744998698',
  android: 'https://play.google.com/store/apps/details?id=com.angel.sketch.app',
}

const detectDeviceFromUserAgent = (userAgent: string) => {
  const ua = userAgent.toLowerCase()

  // iOS devices
  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios'
  }

  // Android devices
  if (/android/.test(ua)) {
    return 'android'
  }

  // Mac devices (could indicate App Store preference)
  // if (/macintosh|mac os x/.test(ua)) {
  //   return 'ios'
  // }

  return null
}

const SketchAppRedirect: NextPageWithLayout<SketchAppRedirectProps> = ({ locale }) => {
  const { t } = useTranslate('common')
  const { isDarkMode } = useThemeContext()

  const handleDownloadClick = (platform: 'apple' | 'android') => {
    window.open(APP_STORE_URLS[platform], '_blank')
  }

  return (
    <>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1745464385/Sketches/app/monster-seo.png',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('sketchAppSeoTitleAngel', 'SKETCH App - Bring your drawings to life | Angel'),
          description: t(
            'sketchAppSeoDescriptionAngel',
            'Download the Sketch App and bring your drawings to life, inspired by the upcoming film SKETCH from Angel.',
          ),
          locale,
          path: '/sketchapp',
        })}
      />
      <div
        className={classNames('pb-24 pt-16 lg:pb-32 lg:pt-24', {
          'bg-core-gray-950': isDarkMode,
          'bg-core-gray-100': !isDarkMode,
        })}
      >
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div
            className={classNames('overflow-hidden rounded-lg shadow-xl', {
              'bg-core-gray-900': isDarkMode,
              'bg-white': !isDarkMode,
            })}
          >
            <div className="p-8 text-center lg:p-12">
              {/* App Icon */}
              <div className="mb-8 flex justify-center">
                <div className="relative h-32 w-32 overflow-hidden rounded-[24px] shadow-lg">
                  <Image src="/v1750454159/sketch/sketch-app-icon-light_1.webp" alt="" width={128} height={128} />
                </div>
              </div>

              {/* Title and Description */}
              <h1
                className={classNames('mb-4 text-4xl font-bold lg:text-5xl', {
                  'text-white': isDarkMode,
                  'text-core-gray-900': !isDarkMode,
                })}
              >
                Download the SKETCH App
              </h1>
              <p
                className={classNames('mb-8 text-xl', {
                  'text-core-gray-300': isDarkMode,
                  'text-core-gray-600': !isDarkMode,
                })}
              >
                Bring your drawings to life with the official SKETCH app
              </p>

              {/* Download Buttons */}
              <div className="flex flex-col justify-center gap-4 sm:flex-row sm:gap-6">
                {/* Apple App Store Button */}
                <button
                  onClick={() => handleDownloadClick('apple')}
                  className="inline-flex items-center justify-center rounded-lg bg-black px-6 py-3 text-white transition-transform focus:outline-none focus:ring-2 focus:ring-core-gray-500 focus:ring-offset-2 hover:scale-105"
                >
                  <svg className="mr-3 h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="text-lg font-semibold">App Store</div>
                  </div>
                </button>

                {/* Google Play Button */}
                <button
                  onClick={() => handleDownloadClick('android')}
                  className="inline-flex items-center justify-center rounded-lg bg-black px-6 py-3 text-white transition-transform focus:outline-none focus:ring-2 focus:ring-core-gray-500 focus:ring-offset-2 hover:scale-105"
                >
                  <svg className="mr-3 h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Get it on</div>
                    <div className="text-lg font-semibold">Google Play</div>
                  </div>
                </button>
              </div>

              {/* Movie Promotion */}
              <div className="mt-12 border-t border-core-gray-200 pt-8 dark:border-core-gray-700">
                <p
                  className={classNames('text-sm', {
                    'text-core-gray-300': isDarkMode,
                    'text-core-gray-600': !isDarkMode,
                  })}
                >
                  <span
                    className={classNames('block font-semibold text-base mb-2', {
                      'text-white': isDarkMode,
                      'text-core-gray-900': !isDarkMode,
                    })}
                  >
                    <i>SKETCH</i> the movie comes exclusively to theaters August 6th, 2025
                  </span>
                  <span className="block">
                    {t(
                      'sketchShortDesc',
                      'A girl’s drawings come to life, and her family comes together to stop the chaos they unleashed.',
                    )}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Use the getLayout pattern to apply the DefaultLayout
SketchAppRedirect.getLayout = (page) => {
  return (
    <DefaultLayout isDarkMode={false} showFooter={true}>
      {page}
    </DefaultLayout>
  )
}
export const getServerSideProps = getServerSidePropsErrorHandler(async ({ locale, req }) => {
  const userAgent = req.headers['user-agent'] || ''
  const device = detectDeviceFromUserAgent(userAgent)

  // Redirect iOS devices to Apple App Store
  if (device === 'ios') {
    return {
      redirect: {
        destination: APP_STORE_URLS.apple,
        permanent: false,
      },
    }
  }

  // Redirect Android devices to Google Play Store
  if (device === 'android') {
    return {
      redirect: {
        destination: APP_STORE_URLS.android,
        permanent: false,
      },
    }
  }

  // For unknown devices, show the fallback page
  return {
    props: {
      locale,
      ...(await loadTranslations(locale, ['common'])),
    },
  }
})

export default SketchAppRedirect
